import { AppContext } from 'context/ContextProvider';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { InfoIcon } from '../../../assets/icons';
import { Button } from '../../../components';
import { ContentSkeleton } from '../../../components/Skeleton';
import ToolTip from '../../../components/Tooltip';
import { getPortfolioDropdownOptions, getPortfolioInfo } from '../../../helpers';
import { Alignment, NavigationPath, PortfolioBalanceInfo } from '../../../types/DomainTypes';
import { buildDisplayText, formatterGbp } from '../../../utils';
import { usePortfolioHoldingsTotals } from 'hooks/usePortfolioHoldingsTotals';
import { usePortfolioBalances } from '../../Portfolio/components/Summary/hooks/usePortfolioBalances';
import { AccountViewType, SlideOutPanelViews, SubjectOptionKeys } from '../types';
import AccountReport from './AccountReport';

enum DisplayTextKeysEnum {
  TOPUP_SLIDEOUT_TITLE = 'account:slideout.topup.title',
  WITHDRAW_FUNDS_TEXT = 'account:text.withdraw_funds',
  PENDING_BALANCE_TOOLTIP = 'pending_balance_tooltip',
  PENDING_BALANCE_TEXT = 'pending_balance_text',
  ACCOUNT_BALANCE_TEXT = 'account_balance_text',
}

const DisplayTextKeys = {
  ...DisplayTextKeysEnum,
  ...PortfolioBalanceInfo,
};

interface OverviewProps {
  openSlideout: (view: SlideOutPanelViews) => void;
  onClose: (nextView?: AccountViewType) => void;
}

const Overview: FC<OverviewProps> = ({ openSlideout, onClose }) => {
  const { t } = useTranslation();
  const { managementFeeUpdates14758 } = useFlags();
  const displayText = useMemo(() => buildDisplayText(Object.values(DisplayTextKeys), 'account:overviewReport', t), [t]);
  const navigate = useNavigate();
  const { portfolioBalances, loading: loadingBalances } = usePortfolioBalances();
  // const portfolioDropdownOptions = useMemo(() => getPortfolioDropdownOptions(portfolioBalances), [portfolioBalances]);
  const [selectedPortfolioBalance] = useState('');
  // const onPortfolioChange = (item: DropdownItem) => {
  //   setSelectedPortfolioBalance(item.value);
  // };

  // useEffect(() => {
  //   if (selectedPortfolioId) {
  //     setSelectedPortfolioBalance(selectedPortfolioId as unknown as string);
  //   } else {
  //     setSelectedPortfolioBalance('');
  //   }
  // }, [selectedPortfolioId]);

  const balanceDetails = useMemo(() => {
    const balanceInfo = portfolioBalances.find((x) => x.portfolioId === selectedPortfolioBalance);

    return {
      pendingBalance: balanceInfo?.balancePending || 0,
      accountBalance: balanceInfo?.balance || 0,
    };
  }, [portfolioBalances, selectedPortfolioBalance]);

  const { grandTotal } = usePortfolioHoldingsTotals();

  const selectedPortfolioInfo = useMemo(() => {
    const portfolios = getPortfolioInfo(portfolioBalances, displayText, selectedPortfolioBalance);

    return {
      ...portfolios,
      info: portfolios.info
        .filter((x) =>
          [
            displayText[PortfolioBalanceInfo.CURRENT_ASSETS_TEXT],
            displayText[PortfolioBalanceInfo.NET_POSITION_TEXT],
          ].includes(x.title),
        )
        .map((x) =>
          x.title === displayText[PortfolioBalanceInfo.CURRENT_ASSETS_TEXT]
            ? {
                ...x,
                title: displayText[PortfolioBalanceInfo.PORTFOLIO_VALUATION_TEXT],
                body: (className = 'text-base') => <span className={className}>{formatterGbp.format(grandTotal)}</span>,
              }
            : x,
        ),
    };
  }, [portfolioBalances, selectedPortfolioBalance, displayText, grandTotal]);

  return (
    <div className="flex flex-col flex-1 py-5 px-8 sm:px-10 lg:px-12 w-full min-h-full bg-gray-50 dark:bg-slate-900 overflow-visible">
      {/* Portfolio Summary Card */}
      <div className="bg-white dark:bg-slate-800 p-4 sm:p-6 mt-5 rounded-2xl mb-8 shadow-sm dark:shadow-slate-900/50 border border-gray-100 dark:border-slate-700">
        {loadingBalances && (
          <div className="flex-1 py-6">
            <ContentSkeleton lines={4} />
          </div>
        )}
        {!loadingBalances && (
          <div className="flex w-full flex-wrap gap-6">
            {/* Portfolio Valuation Grid */}
            <div className="bg-gray-50 dark:bg-slate-700/50 min-w-[360px] w-full flex-1 grid grid-cols-2 gap-4 p-4 rounded-xl">
              {selectedPortfolioInfo.info &&
                selectedPortfolioInfo.info.length > 0 &&
                selectedPortfolioInfo.info.map((pBal, index) => {
                  return (
                    <div
                      key={`portfolio-balance-${index}`}
                      className="w-full overflow-hidden rounded-xl bg-white dark:bg-slate-800 p-4 shadow-sm dark:shadow-slate-900/30"
                    >
                      <div className="flex flex-col">
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                          {pBal.title}
                        </span>
                        <span className="text-md font-semibold text-gray-900 dark:text-white">
                          {pBal.body('text-md font-semibold text-gray-900 dark:text-white')}
                        </span>
                      </div>
                    </div>
                  );
                })}
            </div>
            {managementFeeUpdates14758 && (
              <div style={{ flex: 2 }} className="flex items-center justify-center flex-1 grow-2">
                <div className="w-full p-4 flex flex-nowrap items-center rounded-xl flex-col gap-6 md:flex-row md:gap-0 md:divide-x md:divide-gray-200 dark:md:divide-slate-600">
                  {/* Account Balance */}
                  <div className="flex flex-col px-4 pr-8 flex-1 justify-center items-center md:items-end">
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {displayText[DisplayTextKeys.ACCOUNT_BALANCE_TEXT]}
                    </span>
                    <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                      {formatterGbp.format(balanceDetails.accountBalance)}
                    </div>
                    <Button
                      className="btn text-sm font-medium mt-4 whitespace-nowrap rounded-xl w-fit px-6 py-2 border-2 border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-slate-500 hover:bg-gray-50 dark:hover:bg-slate-700 transition-all"
                      onClick={() => {
                        navigate(NavigationPath.ACCOUNTS, {
                          state: {
                            accountViewType: AccountViewType.CONTACT_US,
                            subject: SubjectOptionKeys.WITHDRAWAL_REQUEST,
                          },
                        });
                      }}
                      props={{
                        name: 'acc_withdrwal',
                      }}
                    >
                      {displayText[DisplayTextKeys.WITHDRAW_FUNDS_TEXT]}
                    </Button>
                  </div>
                  {/* Pending Balance */}
                  <div style={{ flex: 1 }} className="flex flex-col px-4 pl-8 flex-1 items-start justify-center">
                    <div className="flex gap-2 items-center justify-center">
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {displayText[DisplayTextKeys.PENDING_BALANCE_TEXT]}
                      </span>
                      <ToolTip
                        align={Alignment.RIGHT}
                        tooltip={
                          <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 w-[220px] text-sm p-3 rounded-lg shadow-lg text-gray-700 dark:text-gray-300">
                            {displayText[DisplayTextKeys.PENDING_BALANCE_TOOLTIP]}
                          </div>
                        }
                      >
                        <InfoIcon className="cursor-pointer w-5 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors" />
                      </ToolTip>
                    </div>
                    <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                      {formatterGbp.format(balanceDetails.pendingBalance)}
                    </div>
                    <Button
                      className="btn text-sm font-semibold whitespace-nowrap bg-gradient-to-r from-orange to-orange/90 dark:from-teal-500 dark:to-teal-600 rounded-xl mt-4 w-fit px-6 py-2 text-white shadow-lg shadow-orange/25 dark:shadow-teal-500/25 hover:shadow-xl hover:shadow-orange/30 dark:hover:shadow-teal-500/30 transition-all"
                      onClick={() => openSlideout(SlideOutPanelViews.TOP_UP)}
                      props={{
                        name: 'acc_topup',
                      }}
                    >
                      {t('account:text.topup')}
                    </Button>
                  </div>
                </div>
              </div>
            )}
            {!managementFeeUpdates14758 && (
              <Button
                className="btn text-sm font-semibold whitespace-nowrap bg-gradient-to-r from-orange to-orange/90 dark:from-teal-500 dark:to-teal-600 rounded-xl mt-4 w-fit px-6 py-2 text-white shadow-lg shadow-orange/25 dark:shadow-teal-500/25 hover:shadow-xl hover:shadow-orange/30 dark:hover:shadow-teal-500/30 transition-all"
                onClick={() => openSlideout(SlideOutPanelViews.TOP_UP)}
                props={{
                  name: 'acc_topup',
                }}
              >
                {t('account:text.topup')}
              </Button>
            )}
          </div>
        )}
      </div>
      {managementFeeUpdates14758 && <AccountReport openSlideout={openSlideout} onClose={onClose} />}
    </div>
  );
};

export default Overview;
